import React, { useState, useEffect } from 'react';
import { doc, onSnapshot, setDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { COLORS } from '../theme';

function Toggle({ checked, onChange, disabled }) {
  return (
    <div
      role="switch"
      aria-checked={checked}
      aria-disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      style={{
        width: '44px',
        height: '24px',
        borderRadius: '12px',
        backgroundColor: checked ? COLORS.accent : COLORS.border,
        position: 'relative',
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'background-color 0.2s',
        flexShrink: 0,
        opacity: disabled ? 0.5 : 1,
      }}
      title={disabled ? 'Read-only access' : undefined}
    >
      <div style={{
        position: 'absolute',
        top: '2px',
        left: checked ? '22px' : '2px',
        width: '20px',
        height: '20px',
        borderRadius: '50%',
        backgroundColor: '#fff',
        transition: 'left 0.2s',
        boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
      }} />
    </div>
  );
}

function FlagCard({ label, description, control, updatedAt }) {
  return (
    <div style={{
      backgroundColor: COLORS.surface,
      border: `1px solid ${COLORS.border}`,
      borderRadius: '8px',
      padding: '24px',
      marginBottom: '16px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '24px',
    }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: '14px', fontWeight: '600', color: COLORS.textPrimary, marginBottom: '4px' }}>
          {label}
        </div>
        <div style={{ fontSize: '13px', color: COLORS.textSecondary }}>
          {description}
        </div>
        {updatedAt && (
          <div style={{ fontSize: '12px', color: COLORS.textMuted, marginTop: '8px' }}>
            Last updated: {updatedAt.toDate
              ? updatedAt.toDate().toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
              : String(updatedAt)}
          </div>
        )}
      </div>
      <div style={{ flexShrink: 0 }}>
        {control}
      </div>
    </div>
  );
}

/**
 * T117 (Prompt #560): the job-alerts master switch. Reads config/jobAlerts live;
 * writes ONLY through the setJobAlertsEnabled callable (superadmin-only and
 * audited server-side; firestore.rules deny direct client writes). Fail-closed:
 * only enabled === true is shown as ON, matching the server. Turning ON needs a
 * confirmation; turning OFF (the safe direction) doesn't.
 */
export function JobAlertsCard({ isSuperadmin }) {
  const [state, setState] = useState({ loading: true, enabled: false, updatedAt: null, updatedBy: null });
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, 'config', 'jobAlerts'),
      (snap) => {
        const d = snap.exists() ? snap.data() : {};
        setState({ loading: false, enabled: d.enabled === true, updatedAt: d.updatedAt || null, updatedBy: d.updatedBy || null });
      },
      () => setState({ loading: false, enabled: false, updatedAt: null, updatedBy: null }),
    );
    return () => unsub();
  }, []);

  async function setEnabled(enabled) {
    setBusy(true);
    setError('');
    try {
      await httpsCallable(functions, 'setJobAlertsEnabled')({ enabled });
      setConfirming(false);
    } catch (e) {
      setError(e?.message || 'Could not change job alerts.');
    } finally {
      setBusy(false);
    }
  }

  const description = (
    <>
      Master switch for new-job alerts: when OFF, posting a job writes no job_broadcast banners (and so sends no job-alert
      pushes). Superadmin only; every change is audited.
      {state.updatedBy && (
        <div style={{ fontSize: '12px', color: COLORS.textMuted, marginTop: '6px' }} data-testid="job-alerts-changed-by">
          Last changed by: {state.updatedBy}
        </div>
      )}
      {confirming && (
        <div style={{ marginTop: '12px', padding: '12px', border: `1px solid ${COLORS.warning}`, borderRadius: '6px' }} role="alertdialog" aria-label="Confirm turning job alerts on">
          <div style={{ fontSize: '13px', color: COLORS.textPrimary, marginBottom: '8px' }}>
            Turn job alert banners ON? Every new job will notify matching workers from now on.
          </div>
          <button type="button" onClick={() => setEnabled(true)} disabled={busy} style={{ marginRight: '8px' }}>Yes, turn ON</button>
          <button type="button" onClick={() => setConfirming(false)} disabled={busy}>Cancel</button>
        </div>
      )}
      {error && <div style={{ fontSize: '12px', color: COLORS.danger, marginTop: '8px' }} role="alert">{error}</div>}
    </>
  );

  return (
    <FlagCard
      label={`Job alert banners: ${state.loading ? '…' : state.enabled ? 'ON' : 'OFF'}`}
      description={description}
      updatedAt={state.updatedAt}
      control={
        <Toggle
          checked={state.enabled}
          disabled={!isSuperadmin || busy || state.loading}
          onChange={(val) => (val ? setConfirming(true) : setEnabled(false))}
        />
      }
    />
  );
}

export default function FeatureFlagsPage() {
  const { claims } = useAuth();
  const [flags, setFlags] = useState({
    aiScreeningEnabled: true,
    aiScreeningModel: 'rules',
    resumeGenerationEnabled: false,
    adzunaEnabled: true,
    careerjetEnabled: false,
  });
  const [updatedAt, setUpdatedAt] = useState(null);
  const [loading, setLoading] = useState(true);

  const canWrite = claims.accessLevel === 'superadmin' || claims.accessLevel === 'operator';

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'config', 'features'), snap => {
      if (snap.exists()) {
        const data = snap.data();
        setFlags({
          aiScreeningEnabled: data.aiScreeningEnabled !== false,
          aiScreeningModel: data.aiScreeningModel || 'rules',
          resumeGenerationEnabled: data.resumeGenerationEnabled === true,
          adzunaEnabled: data.aggregators?.adzuna?.enabled === true,
          careerjetEnabled: data.aggregators?.careerjet?.enabled === true,
        });
        setUpdatedAt(data.updatedAt || null);
      }
      setLoading(false);
    });
    return () => unsub();
  }, []);

  async function writeFlag(updates) {
    if (!canWrite) return;
    await setDoc(doc(db, 'config', 'features'), {
      ...updates,
      updatedAt: new Date(),
    }, { merge: true });
  }

  const pillStyle = (active) => ({
    padding: '8px 16px',
    borderRadius: '20px',
    fontSize: '13px',
    fontWeight: active ? '600' : '400',
    cursor: canWrite ? 'pointer' : 'not-allowed',
    backgroundColor: active ? COLORS.accent : COLORS.surface,
    color: active ? '#1B1B1F' : COLORS.textSecondary,
    border: `1px solid ${active ? COLORS.accent : COLORS.border}`,
    opacity: canWrite ? 1 : 0.6,
    transition: 'all 0.15s',
  });

  if (loading) {
    return <div style={{ color: COLORS.textSecondary, fontSize: '14px' }}>Loading…</div>;
  }

  return (
    <>
      <div style={{ fontSize: '13px', color: COLORS.textSecondary, marginBottom: '24px' }}>
        Changes take effect immediately — no redeploy required.
        {!canWrite && (
          <span style={{ marginLeft: '12px', color: COLORS.warning, fontSize: '12px' }}>
            ⚠ Read-only access
          </span>
        )}
      </div>

      <JobAlertsCard isSuperadmin={claims.accessLevel === 'superadmin'} />

      <FlagCard
        label="AI Screening"
        description="Enable or disable Gemini AI screening on new applications."
        updatedAt={updatedAt}
        control={
          <Toggle
            checked={flags.aiScreeningEnabled}
            onChange={val => writeFlag({ aiScreeningEnabled: val })}
            disabled={!canWrite}
          />
        }
      />

      <FlagCard
        label="Screening Model"
        description="Select the scoring engine for new applications."
        updatedAt={updatedAt}
        control={
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={() => canWrite && writeFlag({ aiScreeningModel: 'gemini' })}
              style={pillStyle(flags.aiScreeningModel === 'gemini')}
              title={canWrite ? undefined : 'Read-only access'}
            >
              Gemini 2.5 Flash-Lite
            </button>
            <button
              onClick={() => canWrite && writeFlag({ aiScreeningModel: 'rules' })}
              style={pillStyle(flags.aiScreeningModel === 'rules')}
              title={canWrite ? undefined : 'Read-only access'}
            >
              Rule-Based
            </button>
          </div>
        }
      />

      <FlagCard
        label="Resume PDF Generation"
        description="Show resume generation card on worker Profile screen"
        updatedAt={updatedAt}
        control={
          <Toggle
            checked={flags.resumeGenerationEnabled}
            onChange={val => writeFlag({ resumeGenerationEnabled: val })}
            disabled={!canWrite}
          />
        }
      />

      <FlagCard
        label="External Jobs: Adzuna"
        description="Primary third-party job aggregator (Prompt #162). Gates both the daily sync and whether Browse Jobs shows Adzuna listings, independent of Careerjet's own toggle below."
        updatedAt={updatedAt}
        control={
          <Toggle
            checked={flags.adzunaEnabled}
            onChange={val => writeFlag({ aggregators: { adzuna: { enabled: val } } })}
            disabled={!canWrite}
          />
        }
      />

      <FlagCard
        label="External Jobs: Careerjet"
        description="Backup/dormant third-party job aggregator — off by default as of Prompt #162. Gates both the daily sync and whether Browse Jobs shows Careerjet listings, independent of Adzuna's own toggle above."
        updatedAt={updatedAt}
        control={
          <Toggle
            checked={flags.careerjetEnabled}
            onChange={val => writeFlag({ aggregators: { careerjet: { enabled: val } } })}
            disabled={!canWrite}
          />
        }
      />
    </>
  );
}
